import { LinkButton } from '@euler/app/components/LinkButton';
import { StackLayout } from '@euler/app/components/StackLayout';
import { DigitalSignModal } from '@euler/app/components/signature/DigitalSignModal';
import { Typography } from '@euler/app/components/typography';
import { useVoiceFeatures } from '@euler/app/components/voice/useVoiceFeatures';
import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { MediaUploadTaskEvent } from '@euler/app/flows/task/functions/task-detail/MediaFileUploadController';
import { Center } from '@euler/components/Center';
import { StatusColors } from '@euler/components/Colors';
import { Input } from '@euler/components/form/Input';
import { Img } from '@euler/components/image/AdvancedImage';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useGeneralBottomSheetModal } from '@euler/functions/useBottomSheetModal';
import { useAlert } from '@euler/functions/ux/useAlert';
import { useToggleAutomaticKeyboard } from '@euler/lib/keyboard';
import { mimeTypeFromFileUri } from '@euler/utils';
import {
  BottomSheetFooter,
  BottomSheetModal,
  BottomSheetScrollView,
} from '@gorhom/bottom-sheet';
import { Picker } from '@react-native-picker/picker';
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ActivityIndicator,
  Keyboard,
  Platform,
  ScrollView,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from 'styled-components';
import { MaintenancePlanListItem, MaintenancePlanListItemType } from '../types';

const kDefaultUnsolvedReasons = [
  '零件缺货',
  '客户拒绝维修',
  '未查明确切原因',
  '其他',
];

const kSignatureUnavailableReasons = ['客户未在店内', '其他'];

type NotesValues = {
  id: string;
  type: MaintenancePlanListItemType;
  unsolvedReason?: string;
  remark: string | undefined;
  customerSignatureImgUrl?: string;
  customerSignatureUnavailableReason?: string;
  customerSignatureUnavailableRemark?: string;
};

export const MaintenancePlanItemNotesBottomSheet = memo(
  ({
    show,
    target,
    onClose,
    onDone,
    ...props
  }: {
    show?: boolean;
    target?: MaintenancePlanListItem;
    onClose?: () => void;
    onDone?: (values: NotesValues) => void;
  }) => {
    const insets = useSafeAreaInsets();
    const alert = useAlert();
    const { rpx } = useResponsiveContext();
    const ref = useRef<MaintenancePlanItemNotesBottomSheetContentRef>(null);

    const { bottomSheetModalRef, ...bottomSheetProps } =
      useGeneralBottomSheetModal({
        show,
        minSnapPoint: Platform.OS === 'web' ? '55%' : '70%',
        mediumSnapPoint: Platform.OS === 'web' ? '65%' : '80%',
        topMargin: rpx(100),
        dismissWhenPressBackdrop: true,
        enablePanDownToClose: true,
        onDismiss: onClose,
      });

    const onDonePress = useCallback(() => {
      const values = ref.current?.getValues();
      if (values == null) return;
      try {
        onDone?.(values);
        bottomSheetModalRef.current?.dismiss();
      } catch (e) {
        void alert({ title: (e as Error).message });
      }
    }, [alert, bottomSheetModalRef, onDone]);

    const ScrollViewComponent =
      Platform.OS === 'web' ? ScrollView : BottomSheetScrollView;

    const [isKeyboardVisible, setKeyboardVisible] = useState(false);
    useEffect(() => {
      const showListener = Keyboard.addListener('keyboardWillShow', () =>
        setKeyboardVisible(true),
      );
      const hideListener = Keyboard.addListener('keyboardWillHide', () =>
        setKeyboardVisible(false),
      );
      return () => {
        showListener.remove();
        hideListener.remove();
      };
    }, []);

    return (
      <BottomSheetModal
        ref={bottomSheetModalRef}
        keyboardBehavior="extend"
        // eslint-disable-next-line @typescript-eslint/no-shadow
        footerComponent={(props: any) => (
          <BottomSheetFooter {...props}>
            <Center.Touchable
              onPress={onDonePress}
              css={`
                background-color: ${StatusColors.Info};
                height: ${rpx(44) + (isKeyboardVisible ? 0 : insets.bottom)}px;
                padding-bottom: ${isKeyboardVisible ? 0 : insets.bottom}px;
                margin-bottom: ${isKeyboardVisible ? rpx(30) : 0}px;
                margin-left: ${isKeyboardVisible ? rpx(20) : 0}px;
                margin-right: ${isKeyboardVisible ? rpx(20) : 0}px;
                border-radius: ${isKeyboardVisible ? rpx(5) : 0}px;
              `}
            >
              <Typography.Text bold size={rpx(18)} color="#fff">
                保存遗留信息
              </Typography.Text>
            </Center.Touchable>
          </BottomSheetFooter>
        )}
        {...bottomSheetProps}
      >
        {target ? (
          <ScrollViewComponent
            keyboardShouldPersistTaps="handled"
            contentContainerStyle={{
              paddingBottom: rpx(44) + insets.bottom + rpx(20),
            }}
          >
            <MaintenancePlanItemNotesBottomSheetContent
              ref={ref}
              target={target}
              {...props}
            />
          </ScrollViewComponent>
        ) : null}
      </BottomSheetModal>
    );
  },
);
type MaintenancePlanItemNotesBottomSheetContentRef = {
  getValues: () => NotesValues | undefined;
};
const MaintenancePlanItemNotesBottomSheetContent = memo(
  forwardRef<
    MaintenancePlanItemNotesBottomSheetContentRef,
    {
      target: MaintenancePlanListItem;
    }
  >(({ target }, ref) => {
    const { rpx } = useResponsiveContext();
    const theme = useTheme();
    const { isVoiceInputEnabled } = useVoiceFeatures();

    useToggleAutomaticKeyboard(true);

    const alert = useAlert();
    const { storeConfiguration, taskManager } = useTaskContext();

    const requiresCustomerSign =
      target.type === 'pending-issue' &&
      !!storeConfiguration.requiresCustomerSignForPendingIssue;

    const unsolvedReasonList = useMemo(
      () => [
        ...(storeConfiguration.predefinedIssueUnsolvedReasons?.length
          ? storeConfiguration.predefinedIssueUnsolvedReasons
          : kDefaultUnsolvedReasons),
      ],
      [storeConfiguration.predefinedIssueUnsolvedReasons],
    );

    const [selectedUnsolvedReason, setSelectedUnsolvedReason] = useState(
      (target.type === 'pending-issue' || target.type === 'fault') &&
        target.unsolvedReason
        ? target.unsolvedReason
        : unsolvedReasonList[0],
    );
    const [remark, setRemark] = useState(target?.remark ?? '');

    const initialSignImgUrl =
      target.type === 'pending-issue'
        ? target.customerSignatureImgUrl
        : undefined;
    const initialSignUnavailableReason =
      target.type === 'pending-issue'
        ? target.customerSignatureUnavailableReason
        : undefined;
    const initialSignUnavailableRemark =
      target.type === 'pending-issue'
        ? target.customerSignatureUnavailableRemark
        : undefined;

    const [signMode, setSignMode] = useState<'sign' | 'unavailable'>(
      initialSignImgUrl
        ? 'sign'
        : initialSignUnavailableReason
        ? 'unavailable'
        : 'sign',
    );
    const [signImgUrl, setSignImgUrl] = useState<string | undefined>(
      initialSignImgUrl,
    );
    const [signUnavailableReason, setSignUnavailableReason] = useState<
      string | undefined
    >(initialSignUnavailableReason ?? kSignatureUnavailableReasons[0]);
    const [signUnavailableRemark, setSignUnavailableRemark] = useState<string>(
      initialSignUnavailableRemark ?? '',
    );
    const [signModalVisible, setSignModalVisible] = useState(false);
    const [signUploading, setSignUploading] = useState(false);

    const onCaptureSign = useCallback(() => {
      setSignModalVisible(true);
    }, []);

    const onSignDismiss = useCallback(() => {
      setSignModalVisible(false);
    }, []);

    const onSignConfirm = useCallback(
      (uri: string) => {
        setSignUploading(true);
        taskManager.uploadController.enqueue({
          localFileUri: uri,
          contentType: mimeTypeFromFileUri(uri, 'image/jpeg'),
          type: 'image',
          listener: (event: MediaUploadTaskEvent) => {
            if (event.type === 'progress' || event.type === 'queued') return;
            if (event.type === 'finished') {
              setSignImgUrl(event.result.url);
              setSignUploading(false);
              setSignModalVisible(false);
            } else if (event.type === 'error') {
              setSignUploading(false);
              void alert({
                title: `签名上传失败: ${event.error.message ?? '请重试'}`,
              });
            }
          },
        });
      },
      [alert, taskManager.uploadController],
    );

    const onMarkSignUnavailable = useCallback(() => {
      setSignMode('unavailable');
    }, []);

    const onClearSignUnavailable = useCallback(() => {
      setSignMode('sign');
    }, []);

    const getValues = useCallback((): NotesValues | undefined => {
      if (!selectedUnsolvedReason) {
        void alert({ title: '请选择遗留原因!' });
        return;
      }
      if (requiresCustomerSign && signUploading) {
        void alert({ title: '签名上传中, 请稍候!' });
        return;
      }
      const inSignMode = signMode === 'sign';
      const effectiveImgUrl = inSignMode ? signImgUrl : undefined;
      const effectiveUnavailableReason = inSignMode
        ? undefined
        : signUnavailableReason;
      return {
        id: target.id,
        type: target.type,
        unsolvedReason: selectedUnsolvedReason,
        remark: remark.trim() || undefined,
        customerSignatureImgUrl: effectiveImgUrl,
        customerSignatureUnavailableReason: effectiveUnavailableReason,
        customerSignatureUnavailableRemark:
          effectiveUnavailableReason && signUnavailableRemark.trim()
            ? signUnavailableRemark.trim()
            : undefined,
      };
    }, [
      alert,
      remark,
      requiresCustomerSign,
      selectedUnsolvedReason,
      signImgUrl,
      signMode,
      signUnavailableReason,
      signUnavailableRemark,
      signUploading,
      target.id,
      target.type,
    ]);

    useImperativeHandle(ref, () => ({
      getValues,
    }));

    return (
      <StackLayout
        gap={rpx(12)}
        css={`
          padding: ${rpx(15)}px ${rpx(20)}px;
        `}
      >
        <Typography.Title level={1} align="center">
          设置
          <Text
            style={{
              opacity: 0.6,
            }}
          >{`"${target.name}"`}</Text>
          遗留信息
        </Typography.Title>

        <StackLayout gap={rpx(5)}>
          <Typography.Label medium>未解决或遗留原因: </Typography.Label>
          <Picker
            selectedValue={selectedUnsolvedReason}
            onValueChange={value => {
              setSelectedUnsolvedReason(value);
            }}
            style={[
              {
                backgroundColor: '#f5f5f5',
                borderRadius: rpx(8),
                overflow: 'hidden',
              },
              Platform.OS === 'web'
                ? { height: rpx(36), paddingHorizontal: rpx(8), borderWidth: 0 }
                : {},
            ]}
          >
            {unsolvedReasonList.map(item => (
              <Picker.Item
                label={item}
                value={item}
                key={item}
                style={{
                  fontFamily: FontFamily.NotoSans.Regular,
                  fontSize: rpx(14),
                }}
              />
            ))}
          </Picker>
        </StackLayout>

        <View>
          <Typography.Label medium mb={rpx(5)}>
            其他备注:{' '}
          </Typography.Label>
          <Input
            value={remark}
            multiline
            numberOfLines={0}
            onChangeText={setRemark}
            placeholder="相关备注信息"
            withVoiceInput={isVoiceInputEnabled}
            containerStyle={focus => ({
              borderColor: focus ? StatusColors.Info : '#f5f5f5',
            })}
            css={`
              background-color: #f5f5f5;
              border-radius: ${rpx(8)}px;
              font-weight: 500;
              font-size: ${rpx(15)}px;
              height: ${rpx(100)}px;
              padding-left: ${rpx(10)}px;
              padding-right: ${rpx(10)}px;
              padding-top: ${rpx(5)}px;
              padding-bottom: ${rpx(5)}px;
            `}
          />
        </View>

        {requiresCustomerSign && (
          <StackLayout gap={rpx(6)}>
            <StackLayout
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography.Label medium>客户签字确认: </Typography.Label>
              {signMode === 'sign' ? (
                <LinkButton
                  text="客户签字不可用"
                  textStyle={{ fontSize: rpx(12) }}
                  onPress={onMarkSignUnavailable}
                />
              ) : (
                <LinkButton
                  text="改为签字"
                  textStyle={{ fontSize: rpx(12) }}
                  onPress={onClearSignUnavailable}
                />
              )}
            </StackLayout>

            {signMode === 'sign' ? (
              signImgUrl ? (
                <StackLayout
                  direction="row"
                  gap={rpx(8)}
                  alignItems="center"
                  css={`
                    background-color: #f5f5f5;
                    border-radius: ${rpx(8)}px;
                    padding: ${rpx(8)}px;
                  `}
                >
                  <Img
                    uri={signImgUrl}
                    width={rpx(120)}
                    height={rpx(60)}
                    resizeMode="contain"
                  />
                  <LinkButton
                    text="重新签字"
                    textStyle={{ fontSize: rpx(12) }}
                    onPress={onCaptureSign}
                  />
                </StackLayout>
              ) : (
                <TouchableOpacity
                  onPress={onCaptureSign}
                  css={`
                    border-style: dashed;
                    border-width: 1px;
                    border-color: #ccc;
                    height: ${rpx(60)}px;
                    align-items: center;
                    justify-content: center;
                    border-radius: ${rpx(8)}px;
                  `}
                >
                  {signUploading ? (
                    <ActivityIndicator />
                  ) : (
                    <Typography.Label color={theme.link}>
                      点击客户签字
                    </Typography.Label>
                  )}
                </TouchableOpacity>
              )
            ) : (
              <StackLayout gap={rpx(5)}>
                <Typography.Label medium size={rpx(13)}>
                  签字不可用原因:{' '}
                </Typography.Label>
                <Picker
                  selectedValue={signUnavailableReason}
                  onValueChange={value => setSignUnavailableReason(value)}
                  style={[
                    {
                      backgroundColor: '#f5f5f5',
                      borderRadius: rpx(8),
                      overflow: 'hidden',
                    },
                    Platform.OS === 'web'
                      ? {
                          height: rpx(36),
                          paddingHorizontal: rpx(8),
                          borderWidth: 0,
                        }
                      : {},
                  ]}
                >
                  {kSignatureUnavailableReasons.map(item => (
                    <Picker.Item
                      label={item}
                      value={item}
                      key={item}
                      style={{
                        fontFamily: FontFamily.NotoSans.Regular,
                        fontSize: rpx(14),
                      }}
                    />
                  ))}
                </Picker>
                <Input
                  value={signUnavailableRemark}
                  multiline
                  numberOfLines={0}
                  onChangeText={setSignUnavailableRemark}
                  placeholder="备注 (可选)"
                  containerStyle={focus => ({
                    borderColor: focus ? StatusColors.Info : '#f5f5f5',
                  })}
                  css={`
                    background-color: #f5f5f5;
                    border-radius: ${rpx(8)}px;
                    font-weight: 500;
                    font-size: ${rpx(15)}px;
                    height: ${rpx(70)}px;
                    padding-left: ${rpx(10)}px;
                    padding-right: ${rpx(10)}px;
                    padding-top: ${rpx(5)}px;
                    padding-bottom: ${rpx(5)}px;
                  `}
                />
              </StackLayout>
            )}
          </StackLayout>
        )}

        <DigitalSignModal
          visible={signModalVisible}
          loading={signUploading}
          onConfirm={onSignConfirm}
          onDismiss={onSignDismiss}
          onHide={onSignDismiss}
        />
      </StackLayout>
    );
  }),
);
